import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  sortableKeyboardCoordinates
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import IonIcon from '@reacticons/ionicons'
import { useEffect, useState } from 'react'
import DateTimeFormat from '../../../util/DateTimeformat'
import { privatenotes } from '../../../services/data'
import { toast } from 'react-toastify'
import { useLocation, useParams } from 'react-router-dom'

const SortableNote = ({
  note,
  clickupdatebtn,
  clickdetelebtn,
  hoveredNote,
  setHoveredNote,
  setShowModal,
  setpassnoteid,
  ticketid
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id: note.id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1
  }

  return (
    <div ref={setNodeRef} style={style} className='flex flex-col gap-2'>
      {/* Header Section */}
      <div className='flex flex-row items-end justify-between'>
        {/* Timestamp */}
        <p className='text-[12px]'>
          {DateTimeFormat(note.updatedAt).time}-{''}
          {DateTimeFormat(note.updatedAt).date}
        </p>

        {/* Vertical Dots (Options Icon) with Hover Effect */}
        <div
          className='relative cursor-pointer flex items-center p-2 rounded-full hover:bg-gray-200'
          onMouseEnter={() => setHoveredNote(note.id)}
          onMouseLeave={() => setHoveredNote(null)}
          onClick={e => e.stopPropagation()} // Prevent drag when clicking options
        >
          <svg
            width='15'
            height='16'
            viewBox='0 0 15 16'
            fill='none'
            xmlns='http://www.w3.org/2000/svg'
          >
            <path
              d='M7.5 9.40625C8.27665 9.40625 8.90625 8.77665 8.90625 8C8.90625 7.22335 8.27665 6.59375 7.5 6.59375C6.72335 6.59375 6.09375 7.22335 6.09375 8C6.09375 8.77665 6.72335 9.40625 7.5 9.40625Z'
              fill='#373737'
            />
            <path
              d='M7.5 14.0938C8.27665 14.0938 8.90625 13.4642 8.90625 12.6875C8.90625 11.9108 8.27665 11.2812 7.5 11.2812C6.72335 11.2812 6.09375 11.9108 6.09375 12.6875C6.09375 13.4642 6.72335 14.0938 7.5 14.0938Z'
              fill='#373737'
            />
            <path
              d='M7.5 4.71875C8.27665 4.71875 8.90625 4.08915 8.90625 3.3125C8.90625 2.53585 8.27665 1.90625 7.5 1.90625C6.72335 1.90625 6.09375 2.53585 6.09375 3.3125C6.09375 4.08915 6.72335 4.71875 7.5 4.71875Z'
              fill='#373737'
            />
          </svg>

          {/* Dropdown Menu (Shows on Hover) */}
          {hoveredNote === note.id && (
            <div
              onClick={e => e.stopPropagation()}
              className='absolute right-0 mt-2 w-28 py-3 bg-white shadow-md rounded-lg border border-gray-200 z-50'
            >
              <button
                className='w-full text-left text-[12px] px-4 py-2 text-sm hover:bg-gray-100 flex items-center gap-1'
                onClick={e => {
                  e.stopPropagation()
                  clickupdatebtn(note)
                }}
              >
                <IonIcon name='pencil-outline' />
                <p> Update</p>
              </button>
              <button
                className='w-full text-left px-4 py-2 text-[12px] text-sm text-red-600 hover:bg-red-100 flex items-center gap-1'
                onClick={e => {
                  e.stopPropagation()
                  setHoveredNote(null)
                  setShowModal(true)
                  setpassnoteid(note.id)
                }}
              >
                <IonIcon name='trash-outline' />
                <p> Delete</p>
              </button>
            </div>
          )}
        </div>
      </div>
      {/* Note Content Section */}
      <div
        className='p-2 w-full bg-[#F9F9F9] rounded-[10px] cursor-grab max-w-full overflow-hidden '
        {...attributes}
        {...listeners}
      >
        <div className='flex gap-1 items-center'>
          <IonIcon
            name='reorder-three-outline'
            className='text-[16px] flex-shrink-0 w-[16px] min-w-[16px] '
          />
          <p
            className='text-[12px] break-words whitespace-pre-wrap overflow-hidden text-ellipsis max-w-full'
            dangerouslySetInnerHTML={{ __html: note.ticket_notes }}
          />
        </div>
      </div>
    </div>
  )
}

const PrivateNotesWithDraggable = ({
  notes,
  clickupdatebtn,
  clickdetelebtn,
  ticketid
}) => {
  const [passnoteid, setpassnoteid] = useState('')
  const [hoveredNote, setHoveredNote] = useState(null)
  const [showModal, setShowModal] = useState(false)
  const [items, setItems] = useState(() => {
    // Sort notes by position if available
    return [...notes].sort((a, b) => (a.position || 0) - (b.position || 0))
  })
  const notesService = new privatenotes()

  // Update items when notes prop changes
  useEffect(() => {
    setItems(notes)
  }, [notes])

  const location = useLocation()
  const params = useParams()

  // ✅ Extract store_id from URL (params or pathname)
  const getStoreIdFromUrl = () => {
    if (params?.store_id) return params.store_id
    const pathParts = location.pathname.split('/')
    console.log(pathParts[1])
    return pathParts[1] || null
  }

  const storeid = getStoreIdFromUrl()

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates
    })
  )

  const handledeletenotes = async noteid => {
    try {
      const res = await notesService.deleteNote(noteid)
      setShowModal(false)
      window.dispatchEvent(
        new CustomEvent('deleterefresh', {
          detail: { id: ticketid, detail: true }
        })
      )
      toast.success('Note deleted successfully!', {
        position: 'bottom-center'
      })
      clickdetelebtn()
    } catch (error) {
      console.error('Error deleting note:', error)
      toast.error('Failed to delete note. Please try again.')
    }
  }

  function handleDragEnd (event) {
    const { active, over } = event

    if (active.id !== over.id) {
      setItems(items => {
        const oldIndex = items.findIndex(item => item.id === active.id)
        const newIndex = items.findIndex(item => item.id === over.id)

        const newItems = arrayMove(items, oldIndex, newIndex)

        // Prepare the positions object for the API
        const positions = {}
        newItems.forEach((item, index) => {
          positions[String(item.id)] = index + 1 // Positions start from 1
        })

        // Call the API to update positions
        updateNotePositions(positions)

        // Return updated items with correct positions
        return newItems.map((item, index) => ({
          ...item,
          position: index + 1
        }))
      })
    }
  }

  async function updateNotePositions (positions) {
    // console.log('Updating note positions:', positions);
    try {
      const notesService = new privatenotes()
      const response = await notesService.updateNotePositions({
        store_id: storeid,
        ticket_id: ticketid, // Assuming ticketid is available in scope
        positions: positions
      })

      //   console.log('Note positions updated successfully:', response);
      if (response.success) {
        toast.success(response.message, {
          position: 'bottom-center'
        })
      }
    } catch (error) {
      console.error('Error updating note positions:', error)
      toast.error(error.message, {
        position: 'bottom-center'
      })
    }
  }

  return (
    <>
      {items.length > 0 ? (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext items={items} strategy={verticalListSortingStrategy}>
            <div className='flex flex-col gap-y-6 px-3'>
              {items
                .sort((a, b) => a.position - b.position) // Sort by position (ascending)
                .map(note => (
                  <SortableNote
                    key={note.id}
                    note={note}
                    clickupdatebtn={clickupdatebtn}
                    clickdetelebtn={clickdetelebtn}
                    hoveredNote={hoveredNote}
                    setHoveredNote={setHoveredNote}
                    setShowModal={setShowModal}
                    setpassnoteid={setpassnoteid}
                  />
                ))}
            </div>
          </SortableContext>
        </DndContext>
      ) : (
        <p className='text-[12px] text-center'>Notes Not Found</p>
      )}

      {showModal && (
        <div className='fixed inset-0 bg-black bg-opacity-5 flex justify-center items-center z-50'>
          <div className='bg-[#F4F4F4] rounded-[24px] w-[456px] h-[240px] mx-5 p-6 border-[1.6px] border-[#FFFFFF]  shadow[-1px_0px_0px_0px_inset]'>
            <div className='block'>
              <div className='w-full flex justify-end items-start '>
                <svg
                  width='26'
                  height='26'
                  className='cursor-pointer'
                  viewBox='0 0 26 26'
                  fill='none'
                  xmlns='http://www.w3.org/2000/svg'
                  onClick={() => {
                    setShowModal(false)
                  }}
                >
                  <path
                    fill-rule='evenodd'
                    clip-rule='evenodd'
                    d='M7.88702 6.73798C7.56972 6.42067 7.05528 6.42067 6.73798 6.73798C6.42067 7.05528 6.42067 7.56972 6.73798 7.88702L11.851 13L6.73798 18.113C6.42067 18.4303 6.42067 18.9447 6.73798 19.262C7.05528 19.5793 7.56972 19.5793 7.88702 19.262L13 14.149L18.113 19.262C18.4303 19.5793 18.9447 19.5793 19.262 19.262C19.5793 18.9447 19.5793 18.4303 19.262 18.113L14.149 13L19.262 7.88702C19.5793 7.56972 19.5793 7.05528 19.262 6.73798C18.9447 6.42067 18.4303 6.42067 18.113 6.73798L13 11.851L7.88702 6.73798Z'
                    fill='#373737'
                  />
                </svg>
              </div>
              <div className=' mt-2 flex flex-col items-center justify-center gap-3'>
                <p className='font-[800] text-[20px] leading-[38.25px] text-[#252530]'>
                  Delete
                </p>
                <p className='font-[400] text-[16px] leading-[21px]'>
                  Are you sure want to delete
                </p>
                <div className='flex mt-4 gap-4'>
                  <button
                    onClick={() => {
                      setShowModal(false)
                    }}
                    className='bg-[#232328] w-[136px] h-[39px] p-[10px] font-[600] text-[14px] leading-[19.12px] rounded-[39px] text-[#FFFFFF]'
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => handledeletenotes(passnoteid)}
                    className='bg-[#E22400] w-[136px] h-[39px] p-[10px] font-[600] text-[14px] leading-[19.12px] rounded-[39px] text-[#FFFFFF]'
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default PrivateNotesWithDraggable
